/*
src/data/Workspace.js
wgbh-skydiagram-testing
astro.unl.edu
2019-07-14
*/


/*

	To create a workspace, first create a Workspace instance, then call init. The init method is
asynchronous. The 'ready' callback will be made when the workspace is ready for use.

	These methods are available after the workspace is ready:
		getParamsManager
		getOptionsManager
		getSunImageBank
		getMoonImageBank
		getGroundImageBank
		getForegroundObjectsImageBank

	If possible, the workspace will be recreated from local browser storage (using an IndexedDB
database). Otherwise, a default workspace is created. If local storage is available, the workspace
will automatically back itself up.

*/

import {SDImageBank} from './SDImageBank.js';
import {ParamsManager} from './ParamsManager.js';
import {OptionsManager} from './OptionsManager.js';


import {CallbackMakerMixin} from '../utils/CallbackMakerMixin.js';


const DATABASE_NAME = 'wgbh-skydiagram-testing';


export class Workspace {


	constructor(BASE_PATH) {

		this._BASE_PATH = BASE_PATH;

		this._isReady = false;

		this._db = null;

		this._paramsManager = null;
		this._optionsManager = null;

		this._sunImageBank = null;	
		this._moonImageBank = null;	
		this._groundImageBank = null;
		this._foregroundObjectsImageBank = null;
		
		this._init();

		CallbackMakerMixin.apply(this);
	}

	_init() {
		this._useDefaults = false;
		// The first step is to attempt to open the database.
		let r = window.indexedDB.open(DATABASE_NAME, 1);
		r.onsuccess = (e) => {
			this._db = e.target.result;
			this._finishInitWithDatabase();
		};
		r.onerror = (evt) => {
			console.group('Failed to open database.');
			console.error('Workspace will use default values, but changes will not be saved. Error event follows...');
			console.error(evt);
			console.groupEnd();
			this._finishInitWithNoDatabase();
		};
		r.onupgradeneeded = (e) => {
			let db = e.target.result;
			ParamsManager.upgradeDatabase(db);
			OptionsManager.upgradeDatabase(db);
			SDImageBank.upgradeDatabase(db, 'sun');
			SDImageBank.upgradeDatabase(db, 'moon');
			SDImageBank.upgradeDatabase(db, 'ground');
			SDImageBank.upgradeDatabase(db, 'foreground-objects');
			this._useDefaults = true;
		};
	}

	_finishInitWithDatabase() {

		this._onItemReady = this._onItemReady.bind(this);
		this._itemCountdown = 0;

		this._moonImageBank = new SDImageBank(this, 'moon', {database: this._db, useDefaults: this._useDefaults});
		if (!this._moonImageBank.getIsReady()) {
			this._itemCountdown += 1;
			this._moonImageBank.registerCallback('ready', this._onItemReady);
		}	

		this._sunImageBank = new SDImageBank(this, 'sun', {database: this._db, useDefaults: this._useDefaults});
		if (!this._sunImageBank.getIsReady()) {
			this._itemCountdown += 1;
			this._sunImageBank.registerCallback('ready', this._onItemReady);
		}

		this._groundImageBank = new SDImageBank(this, 'ground', {database: this._db, useDefaults: this._useDefaults});
		if (!this._groundImageBank.getIsReady()) {
			this._itemCountdown += 1;
			this._groundImageBank.registerCallback('ready', this._onItemReady);
		}

		this._foregroundObjectsImageBank = new SDImageBank(this, 'foreground-objects', {database: this._db, useDefaults: this._useDefaults});
		if (!this._foregroundObjectsImageBank.getIsReady()) {
			this._itemCountdown += 1;
			this._foregroundObjectsImageBank.registerCallback('ready', this._onItemReady);
		}

		this._paramsManager = new ParamsManager(this, {database: this._db, useDefaults: this._useDefaults});
		if (!this._paramsManager.getIsReady()) {
			this._itemCountdown += 1;
			this._paramsManager.registerCallback('ready', this._onItemReady);
		} else {
			this._paramsManager.resolveReferences();
		}

		this._optionsManager = new OptionsManager(this, {database: this._db, useDefaults: this._useDefaults});
		if (!this._optionsManager.getIsReady()) {
			this._itemCountdown += 1;
			this._optionsManager.registerCallback('ready', this._onItemReady);
		}

		this._isReady = (this._itemCountdown === 0);
	}

	getIsReady() {
		return this._isReady;
	}

	_onItemReady(type, caller, data) {
		this._itemCountdown -= 1;
		if (this._itemCountdown === 0) {
			this._paramsManager.resolveReferences();
			this._isReady = true;
			this.makeCallback('ready');
		}
	}


	_finishInitWithNoDatabase() {
		// Initialize with default values.

		this._sunImageBank = new SDImageBank(this, 'sun', {def: SDImageBank.getDefaultDef('sun')});
		this._moonImageBank = new SDImageBank(this, 'moon', {def: SDImageBank.getDefaultDef('moon')});
		this._groundImageBank = new SDImageBank(this, 'ground', {def: SDImageBank.getDefaultDef('ground')});
		this._foregroundObjectsImageBank = new SDImageBank(this, 'foreground-objects', {def: SDImageBank.getDefaultDef('foreground-objects')});

		this._paramsManager = new ParamsManager(this, {def: ParamsManager.getDefaultDef()});
		this._paramsManager.resolveReferences();

		this._optionsManager = new OptionsManager(this);

		this._isReady = true;
	}


	getBasePath() {
		return this._BASE_PATH;
	}

	getIsReady() {
		return this._isReady;
	}

	getParamsManager() {
		return this._paramsManager;
	}

	getOptionsManager() {
		return this._optionsManager;
	}

	getSunImageBank() {
		return this._sunImageBank;
	}

	getMoonImageBank() {
		return this._moonImageBank;
	}

	getGroundImageBank() {
		return this._groundImageBank;
	}

	getForegroundObjectsImageBank() {
		return this._foregroundObjectsImageBank;
	}

	_touch() {
		console.warn('Call to Workspace _touch.');
		// TODO: remove or use
	}
}

